/**
 * Was die Strecke „Zeitraum“ eines Projekts zeigt (#220): welcher Satz darunter steht, wie weit
 * die Welle gefüllt ist und ob die Marke „heute“ darauf sitzt.
 *
 * Gerechnet in **Kalendertagen**, nicht in Stunden: „Noch 3 Tage“ gilt den ganzen Tag lang.
 */
export type TimelineCaption =
  | { kind: 'none' }
  /** Der Start liegt noch vor uns. */
  | { kind: 'upcoming'; days: number }
  /** Ein Ende steht fest und ist noch nicht erreicht. */
  | { kind: 'left'; days: number }
  | { kind: 'endsToday' }
  | { kind: 'over'; days: number }
  /** Nur ein Start, und der ist erreicht. */
  | { kind: 'openEnded'; since: Date }

export interface TimelineState {
  caption: TimelineCaption
  /** Verstrichener Anteil 0..1 — `null`, wo es ohne Start UND Ende keine Strecke gibt. */
  share: number | null
  /** Die Marke „heute“ — nur, solange das Projekt zwischen Start und Ende läuft. */
  marker: boolean
}

const DAY = 24 * 60 * 60 * 1000

function dayOf(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

/** Ganze Kalendertage von `from` bis `to`; `round` fängt die Stunde der Zeitumstellung ab. */
function daysBetween(from: Date, to: Date): number {
  return Math.round((dayOf(to) - dayOf(from)) / DAY)
}

export function timelineState(start: Date | null, end: Date | null, today: Date): TimelineState {
  if (start && daysBetween(today, start) > 0) {
    return { caption: { kind: 'upcoming', days: daysBetween(today, start) }, share: end ? 0 : null, marker: false }
  }
  if (end) {
    const left = daysBetween(today, end)
    const caption: TimelineCaption = left > 0
      ? { kind: 'left', days: left }
      : left === 0 ? { kind: 'endsToday' } : { kind: 'over', days: -left }
    if (!start) return { caption, share: null, marker: false }
    const span = daysBetween(start, end)
    const share = span <= 0 || left <= 0 ? 1 : daysBetween(start, today) / span
    return { caption, share, marker: left > 0 }
  }
  if (start) return { caption: { kind: 'openEnded', since: start }, share: null, marker: false }
  return { caption: { kind: 'none' }, share: null, marker: false }
}
